import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { SpeedInsights } from '@vercel/speed-insights/react';
import './App.css';
import './Dashboard.css'; // Import the new dashboard styles
import './components/FocusFeature.css'; // Search focus chips + collapsed sections
import ResultsPage from './components/ResultsPage';
import Header from './components/Header';
import ContactPage from './components/ContactPage';
import FeedbackPage from './components/FeedbackPage';
import TermsOfUse from './components/TermsOfUse';
import PrivacyPolicy from './components/PrivacyPolicy';
import GovMapService from './services/GovMapService';
import ArchitecturalPlansService from './services/ArchitecturalPlansService';
import FeasibilityService from './services/FeasibilityService';
import { prefetchMarketPricing } from './services/FeasibilityAdapter.js';
import { buildSearchResultsForFeasibility } from './utils/buildSearchResultsForFeasibility';
import { buildSourceHealthFromApiErrors } from './utils/sourceHealth';
import config from './config/api';
import CustomAutocomplete from './components/CustomAutocomplete';
import LegalModal from './components/LegalModal';
import { TERMS_OF_USE, PRIVACY_POLICY, ACCESSIBILITY_STATEMENT } from './data/legalContent';
import { LoadingShowcase, SearchInputLoading } from './components/LoadingAnimations';
import { ResultsPageShimmer } from './components/ShimmerComponents';
import DisclaimerBanner from './components/DisclaimerBanner';
import AnalyticsService from './services/AnalyticsService';
import RecentSearches from './components/RecentSearches';
import useSearchHistory from './hooks/useSearchHistory';
import AboutPage from './components/AboutPage';
import ProjectPromotionPage from './components/ProjectPromotionPage';
import PlanDetailPage from './components/PlanDetailPage';
import PermitDetailPage from './components/PermitDetailPage';

// Get Google Maps API key from environment variables
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Optional "focus" for an address search: the default 'all' shows the full
// results as before; the others lead with one aspect and collapse the rest.
const FOCUS_OPTIONS = [
  { id: 'all', label: 'תמונה כללית' },
  { id: 'financials', label: 'שווי וכדאיות' },
  { id: 'plans', label: 'תוכניות והיתרים' },
  { id: 'renewal', label: 'התחדשות עירונית' },
  { id: 'alerts', label: 'התראות חכמות' },
];

if (!GOOGLE_MAPS_API_KEY) {
  console.error('Google Maps API key is not set! Please add REACT_APP_GOOGLE_MAPS_API_KEY to your environment variables.');
}

// Helper to get initial state from URL params
const getInitialStateFromUrl = () => {
  console.log('🔗 getInitialStateFromUrl called');
  console.log('🔗 window.location.search:', window.location.search);
  
  const params = new URLSearchParams(window.location.search);
  const urlAddress = params.get('address');
  const urlGush = params.get('gush');
  const urlHelka = params.get('helka');
  const urlFocus = params.get('focus') || 'all';

  console.log('🔗 Parsed URL params:', { urlAddress, urlGush, urlHelka, urlFocus });

  if (urlAddress) {
    const decoded = decodeURIComponent(urlAddress);
    console.log('🔗 Found address param:', decoded);
    return {
      hasUrlParams: true,
      mode: 'address',
      address: decoded,
      gush: '',
      helka: '',
      submittedAddress: decoded,
      focus: urlFocus
    };
  } else if (urlGush && urlHelka) {
    console.log('🔗 Found gush/helka params:', urlGush, urlHelka);
    return {
      hasUrlParams: true,
      mode: 'parcel',
      address: '',
      gush: urlGush,
      helka: urlHelka,
      submittedAddress: `גוש ${urlGush}, חלקה ${urlHelka}`,
      focus: 'all'
    };
  }
  console.log('🔗 No URL params found');
  return { hasUrlParams: false, mode: 'address', address: '', gush: '', helka: '', submittedAddress: '', focus: 'all' };
};

const mapTaRelevantToPlanItems = (taRelevant) => {
  if (!taRelevant) return [];
  const normalizeObjectivesText = (value) =>
    String(value || '')
      .replace(/\s*\^\s*/g, '\n')
      .replace(/\r\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  const isPlaceholderObjectives = (text) => {
    const normalized = String(text || '').trim().replace(/\s+/g, ' ');
    if (!normalized) return true;
    return (
      normalized === '-' ||
      normalized === '—' ||
      normalized.toLowerCase() === 'n/a' ||
      normalized === 'לא ידוע' ||
      normalized === 'לא זמין' ||
      normalized === 'אין נתונים' ||
      normalized === 'אין מידע'
    );
  };
  const mapPlan = (item, i, scope = 'applies') => ({
    id: `ta-plan-${scope}-${item.sourceLayer || 'x'}-${item.sourceObjectId || i}`,
    title: item.title || 'תוכנית עירונית',
    status: item.status || 'לא ידוע',
    statusType: item.statusType || 'unknown',
    planNumber: item.planNumber || String(item.sourceObjectId || ''),
    planUrl: item.planUrl || null,
    planType: item.planType || 'תוכנית בניין עיר',
    planSubtype: item.planSubtype || null,
    objectives: (() => {
      const candidate = normalizeObjectivesText(item.mavatObjectivesFull || item.iplanObjectives || item.objectives || '');
      if (isPlaceholderObjectives(candidate)) return null;
      return candidate || null;
    })(),
    location: item.location || null,
    approvingAuthority: item.approvingAuthority || null,
    canIssuePermits: item.canIssuePermits || false,
    municipality: 'תל אביב-יפו',
    municipalSource: 'תל אביב',
    county: 'תל אביב-יפו',
    dataSource: 'Tel Aviv DB',
    submissionDate: item.submissionDate || null,
    approvalDate: item.approvalDate || null,
    distanceMeters: item.distanceMeters ?? null,
    geometryJson: item.geometryJson || null,
    geometryWkid: item.geometryWkid || null,
    bbox: item.bbox || null,
    isApplicableToAddress: scope === 'applies',
    isNearbyToAddress: scope !== 'applies',
    isTelAvivMunicipal: false,
    isTelAvivOutlinePlan: true,
    isKlal: Boolean(item.isKlal),
    isRealData: true,
    _taEntityType: 'plan',
    _taSourceLayer: item.sourceLayer,
    _taSourceObjectId: item.sourceObjectId,
  });
  const mergedPlans = [...(taRelevant?.plans?.applies || []).map((item, i) => mapPlan(item, i, 'applies'))];

  const mapPermit = (item, i, scope = 'applies') => ({
    id: `ta-permit-${scope}-${item.sourceLayer || 'x'}-${item.sourceObjectId || i}`,
    title: item.title || 'בקשה/היתר עירוני',
    status: item.status || 'לא ידוע',
    statusType: item.statusType || 'unknown',
    requestNumber: item.requestNumber || null,
    permitNumber: item.permitNumber || null,
    planNumber: item.requestNumber || item.permitNumber || String(item.sourceObjectId || ''),
    planUrl: item.planUrl || null,
    planType: item.requestType || 'היתר בנייה',
    requestType: item.requestType || 'היתר בנייה',
    content: item.content || null,
    address: item.address || null,
    municipality: 'תל אביב-יפו',
    municipalSource: 'תל אביב',
    county: 'תל אביב-יפו',
    dataSource: 'Tel Aviv DB',
    submissionDate: item.submissionDate || null,
    approvalDate: item.approvalDate || null,
    expiryDate: item.expiryDate || null,
    distanceMeters: item.distanceMeters ?? null,
    isApplicableToAddress: scope === 'applies',
    isNearbyToAddress: scope !== 'applies',
    isTelAvivPermit: true,
    isTelAvivMunicipal: true,
    isRealData: true,
    _taEntityType: 'permit',
    _taSourceLayer: item.sourceLayer,
    _taSourceObjectId: item.sourceObjectId,
  });
  const mergedPermits = [...(taRelevant?.permits?.applies || []).map((item, i) => mapPermit(item, i, 'applies'))];

  return [...mergedPermits, ...mergedPlans];
};

const normalizePlanCode = (value) =>
  String(value || '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/[״"]/g, '')
    .toLowerCase();

const filterTelAvivMunicipalByApplicability = (plans, taRelevant) => {
  if (!Array.isArray(plans) || !taRelevant) return Array.isArray(plans) ? plans : [];

  const applicablePlanNumbers = new Set(
    (taRelevant?.plans?.applies || [])
      .map((p) => normalizePlanCode(p.planNumber))
      .filter(Boolean)
  );

  const applicablePermitNumbers = new Set(
    (taRelevant?.permits?.applies || [])
      .flatMap((p) => [p.requestNumber, p.permitNumber])
      .map((n) => normalizePlanCode(n))
      .filter(Boolean)
  );

  return plans.filter((plan) => {
    // Explicit applicability flags always win.
    if (plan?.isApplicableToAddress) return true;

    // Only gate DB-injected ta-relevant entities.
    // Keep legacy/live municipal API results as-is (old behavior).
    const isTaRelevantEntity = Boolean(
      plan?._taEntityType ||
      String(plan?.dataSource || '').toLowerCase() === 'tel aviv db'
    );
    if (!isTaRelevantEntity) return true;

    const isTelAvivPermit = Boolean(
      plan?.isTelAvivPermit ||
      (plan?.isTelAvivMunicipal && (plan?.requestNumber || plan?.permitNumber))
    );
    const isTelAvivPlan = Boolean(
      plan?.isTelAvivOutlinePlan ||
      (plan?.isTelAvivMunicipal && !isTelAvivPermit)
    );

    if (!isTelAvivPermit && !isTelAvivPlan) return true;

    if (isTelAvivPermit) {
      const permitCode = normalizePlanCode(plan.requestNumber || plan.permitNumber || plan.planNumber);
      if (permitCode && applicablePermitNumbers.has(permitCode)) return true;
      return false;
    }

    const planCode = normalizePlanCode(plan.planNumber);
    if (planCode && applicablePlanNumbers.has(planCode)) return true;

    return false;
  });
};

function App() {
  const [pathname, setPathname] = useState(typeof window !== 'undefined' ? window.location.pathname : '/');
  const isPlanDetailRoute = pathname.startsWith('/plans/');
  const isPermitDetailRoute = pathname.startsWith('/permits/');
  const isDetailRoute = isPlanDetailRoute || isPermitDetailRoute;

  useEffect(() => {
    const onRouteChange = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onRouteChange);
    return () => window.removeEventListener('popstate', onRouteChange);
  }, []);

  useEffect(() => {
    AnalyticsService.trackAppLoaded();
  }, []);

  useEffect(() => {
    AnalyticsService.trackRouteView(pathname);
  }, [pathname]);

  // Get initial state from URL params (runs once)
  const initialUrlState = React.useMemo(() => {
    const result = getInitialStateFromUrl();
    console.log('🔗 initialUrlState (useMemo):', result);
    return result;
  }, []);
  
  console.log('🔗 App rendering, initialUrlState.hasUrlParams:', initialUrlState.hasUrlParams);
  
  const [address, setAddress] = useState(initialUrlState.address);
  const [loading, setLoading] = useState(initialUrlState.hasUrlParams);
  const [showResults, setShowResults] = useState(initialUrlState.hasUrlParams);
  const [searchResults, setSearchResults] = useState(initialUrlState.hasUrlParams ? null : []);
  const [selectedAddress, setSelectedAddress] = useState(initialUrlState.submittedAddress);
  const [propertyInfo, setPropertyInfo] = useState(null);
  const [searchMode, setSearchMode] = useState(initialUrlState.mode);
  const [gush, setGush] = useState(initialUrlState.gush);
  const [helka, setHelka] = useState(initialUrlState.helka);
  const [addressValidationError, setAddressValidationError] = useState('');
  const [parcelValidationError, setParcelValidationError] = useState('');
  const [apiErrors, setApiErrors] = useState({}); 
  const [feasibilityData, setFeasibilityData] = useState(null);
  const [feasibilityOverrides, setFeasibilityOverrides] = useState({});
  const [feasibilityOverrideBusy, setFeasibilityOverrideBusy] = useState(false);
  const feasibilityOverridesRef = useRef({});
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [aiAnalysisUnlocked, setAiAnalysisUnlocked] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(initialUrlState.hasUrlParams ? 'מתחיל חיפוש...' : '');
  const [activeTab, setActiveTabRaw] = useState('property');
  const setActiveTab = useCallback((tab) => {
    setActiveTabRaw(tab);
    AnalyticsService.trackTabChange(tab);
    // Navigating to a tab should always land at the top, not wherever the
    // previous page was scrolled to.
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  }, []);

  // The homepage is navy, but the app/body background is light — so navigating
  // TO the homepage briefly flashes white before the navy content paints. Tag
  // the body while on the homepage so the underlying bg is navy (no flash).
  const isHomepageView = !isPlanDetailRoute && !isPermitDetailRoute && !showResults
    && !['contact', 'feedback', 'about', 'project-promotion'].includes(activeTab);
  useEffect(() => {
    document.body.classList.toggle('app-on-homepage', isHomepageView);
    return () => document.body.classList.remove('app-on-homepage');
  }, [isHomepageView]);
  const [submittedAddress, setSubmittedAddress] = useState(initialUrlState.submittedAddress);
  const [resultsSearchMode, setResultsSearchMode] = useState(initialUrlState.mode);
  // 'all' = full results (default); otherwise lead with one aspect in results.
  const [searchFocus, setSearchFocus] = useState(initialUrlState.focus || 'all');
  const [resultsFocus, setResultsFocus] = useState(initialUrlState.focus || 'all');
  const [legalModalState, setLegalModalState] = useState({ isOpen: false, type: null });
  const [isScrolled, setIsScrolled] = useState(false);
  const isScrolledRef = useRef(false);
  const homepageContextRef = useRef(null);
  const [homepageCardIndex, setHomepageCardIndex] = useState(0);
  const [buttonAnimationComplete, setButtonAnimationComplete] = useState(true);
  const [searchKey, setSearchKey] = useState(0); // Forces loading component remount
  const [formExpanding, setFormExpanding] = useState(false); // For expand animation

  // Search history
  const { history: searchHistory, addEntry: addSearchHistory, removeEntry: removeSearchHistory, clearHistory: clearSearchHistory } = useSearchHistory();

  // Newsletter form state

  // Track scroll state — synchronous (no rAF) to stay in lock-step with
  // Header.js's own scroll handler and avoid 1-frame desync that shows
  // RecentSearches briefly inside the collapsed header.
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      if (!isScrolledRef.current && y > 100) {
        isScrolledRef.current = true;
        setIsScrolled(true);
      } else if (isScrolledRef.current && y < 30) {
        isScrolledRef.current = false;
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!aiRecommendation) setAiAnalysisUnlocked(false);
  }, [aiRecommendation]);

  useEffect(() => {
    feasibilityOverridesRef.current = feasibilityOverrides;
  }, [feasibilityOverrides]);

  const applyFeasibilityOverrides = useCallback(
    async (patch) => {
      if (!patch || typeof patch !== 'object') return;
      if (!propertyInfo || !searchResults) {
        console.warn('applyFeasibilityOverrides: missing propertyInfo or searchResults');
        return;
      }
      const merged = { ...feasibilityOverridesRef.current, ...patch };
      feasibilityOverridesRef.current = merged;
      setFeasibilityOverrides(merged);
      setFeasibilityOverrideBusy(true);
      try {
        const sr = buildSearchResultsForFeasibility({
          address: submittedAddress,
          propertyInfo,
          results: searchResults,
        });
        const result = await FeasibilityService.calculateFeasibilityScore(sr, merged, setLoadingStatus);
        if (result.success) {
          setFeasibilityData(result);
        }
      } catch (e) {
        console.error('Feasibility override recalculation failed:', e);
      } finally {
        setFeasibilityOverrideBusy(false);
        setLoadingStatus('');
      }
    },
    [propertyInfo, searchResults, submittedAddress]
  );

  useEffect(() => {
    if (activeTab === 'project-promotion') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  const openLegalModal = (type) => {
    setLegalModalState({ isOpen: true, type });
  };

  const closeLegalModal = () => {
    setLegalModalState({ isOpen: false, type: null });
  };

  useEffect(() => {
    if (addressValidationError) {
      const timer = setTimeout(() => {
        setAddressValidationError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [addressValidationError]);

  useEffect(() => {
    if (parcelValidationError) {
      const timer = setTimeout(() => {
        setParcelValidationError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [parcelValidationError]);

  const validateAddress = (addressText) => {
    if (!addressText.trim()) return 'נא הזן כתובת';
    const hasNumbers = /\d/.test(addressText);
    if (!hasNumbers) return 'נא הזן כתובת מלאה כולל מספר בית';
    if (addressText.trim().length < 5) return 'נא הזן כתובת מפורטת יותר';
    return null;
  };

  const initializeGoogleMaps = useCallback(async () => {
    try {
      const loader = new Loader({
        apiKey: GOOGLE_MAPS_API_KEY,
        version: 'weekly',
        libraries: ['places'],
        language: 'he',
        region: 'IL'
      });
      await loader.load();
    } catch (error) {
      console.error('❌ Error loading Google Maps:', error);
    }
  }, []);

  useEffect(() => {
    initializeGoogleMaps();
  }, [initializeGoogleMaps]);

  // Handle URL parameters for shareable links
  const urlParamsProcessedRef = useRef(false);

  // Generate shareable URL
  const generateShareUrl = () => {
    const baseUrl = window.location.origin + window.location.pathname;
    if (resultsSearchMode === 'parcel' && propertyInfo?.gush && propertyInfo?.helka) {
      return `${baseUrl}?gush=${propertyInfo.gush}&helka=${propertyInfo.helka}`;
    } else if (submittedAddress) {
      return `${baseUrl}?address=${encodeURIComponent(submittedAddress)}`;
    }
    return baseUrl;
  };

  // Retarget the focus of results that are already on screen (focus chips in
  // the docked strip + the overview's "יכול לעניין אותך גם" cards).
  const handleResultsFocusChange = (focusId) => {
    setSearchFocus(focusId);
    setResultsFocus(focusId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Copy share URL to clipboard
  const handleShare = async () => {
    const url = generateShareUrl();
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch (err) {
      console.error('Failed to copy URL:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    }
  };

  // Execute search if URL params were present (UI state already set via initialUrlState)
  useEffect(() => {
    console.log('🔗 useEffect running, hasUrlParams:', initialUrlState.hasUrlParams, 'processed:', urlParamsProcessedRef.current);
    
    // Only run if we have URL params
    if (!initialUrlState.hasUrlParams) {
      console.log('🔗 No URL params, skipping search');
      return;
    }
    
    // Only run once
    if (urlParamsProcessedRef.current) {
      console.log('🔗 Already processed, skipping');
      return;
    }
    urlParamsProcessedRef.current = true;
    
    console.log('🔗 URL params detected, executing search:', initialUrlState);
    
    const executeUrlSearch = async () => {
      const { mode, address: urlAddress, gush: urlGush, helka: urlHelka } = initialUrlState;
      
      let propertyData = null;
      let searchAddressValue = urlAddress || null;
      
      if (mode === 'parcel') {
        propertyData = {
          gush: urlGush,
          helka: urlHelka,
          subHelka: null,
          municipality: null,
          fullAddress: `גוש ${urlGush}, חלקה ${urlHelka}`,
          searchAddress: `גוש ${urlGush}, חלקה ${urlHelka}`,
          coordinates: null,
          isMockData: false,
          confidence: 1.0,
          dataSource: 'Direct Parcel Input'
        };
        searchAddressValue = `גוש ${urlGush}, חלקה ${urlHelka}`;
        
        // Enrich parcel data with coordinates and municipality
        try {
          setLoadingStatus('מאתר נתוני חלקה...');
          const parcelSearchResponse = await fetch(`${config.apiBaseUrl}/api/search`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              searchText: `${urlGush} ${urlHelka}`,
              language: 'he',
              filterType: 'parcel',
              isAccurate: false,
              maxResults: 5
            })
          });
          
          if (parcelSearchResponse.ok) {
            const parcelSearchData = await parcelSearchResponse.json();
            if (parcelSearchData.results && parcelSearchData.results.length > 0) {
              const firstResult = parcelSearchData.results[0];
              const coordMatch = firstResult.shape?.match(/POINT\(([0-9.]+)\s+([0-9.]+)\)/);
              if (coordMatch) {
                const coords = { x: parseFloat(coordMatch[1]), y: parseFloat(coordMatch[2]) };
                propertyData.coordinates = coords;
                console.log('📍 Got parcel coordinates:', coords);
                
                setLoadingStatus('מאחזר נתוני רשות מקומית...');
                const [advancedData, locateParcel] = await Promise.all([
                  GovMapService.fetchAdvancedLayers(coords).catch(() => null),
                  GovMapService.getParcelDataByCoordinates(coords).catch(() => null),
                ]);
                if (locateParcel) {
                  propertyData.govmapParcelObjectId = locateParcel.objectid;
                  propertyData.locateParcelRingWm = locateParcel.locateParcelRingWm;
                }
                if (advancedData) {
                  propertyData.municipality = advancedData.municipality;
                  propertyData.advancedLayers = advancedData;
                  console.log('🏛️ Got advanced data for parcel:', advancedData);
                }
              }
            }
          }
        } catch (error) {
          console.log('⚠️ Could not enrich parcel data:', error.message);
        }
      }

      try {
        // Fetch property info (only for address mode)
        if (mode === 'address') {
          setLoadingStatus('מאתר פרטי נכס...');
          propertyData = await GovMapService.getPropertyInfo(searchAddressValue);
          console.log('🔗 GovMap data received:', propertyData);
        }
        
        setPropertyInfo(propertyData);

        // Focus no longer gates fetching: the focus chips retarget live results
        // and the overview cross-links into every subject, so all sections must
        // have data regardless of the focus the search started from.
        const wantMarketFeasibility = true;
        const wantTaRelevant = true;

        {
          const prefetchAddr =
            propertyData?.fullAddress ||
            (searchAddressValue && !String(searchAddressValue).startsWith('גוש ')
              ? searchAddressValue
              : null) ||
            (urlAddress && !String(urlAddress).startsWith('גוש ') ? urlAddress : null);
          if (prefetchAddr && wantMarketFeasibility) prefetchMarketPricing(prefetchAddr);
        }

        // Fetch architectural plans
        setLoadingStatus('מחפש תוכניות ממקורות רשמיים...');
        const plansResult = await ArchitecturalPlansService.getRealArchitecturalPlans(
          propertyData.gush, 
          propertyData.helka,
          propertyData.coordinates,
          mode === 'address' ? urlAddress : null,
          setLoadingStatus,
          (partialResult) => {
            if (!partialResult) return;
            setSearchResults(prev => ({
              ...(prev || {}),
              ...partialResult,
              plans: partialResult.plans || prev?.plans || []
            }));
            if (partialResult.buildingData) {
              setPropertyInfo(prev => {
                if (!prev) return prev;
                const next = { ...prev, buildingData: partialResult.buildingData };
                if (partialResult.buildingData.dataSource?.includes('Tel Aviv')) {
                  next.municipality = 'תל אביב-יפו';
                }
                return next;
              });
            }
            if (partialResult.reverseLookupAddress && mode === 'parcel') {
              setSelectedAddress(partialResult.reverseLookupAddress);
            }
          },
          {
            locateParcelRingWm: propertyData.locateParcelRingWm,
            govmapParcelObjectId: propertyData.govmapParcelObjectId,
            parcelCadastreCentroidItm: propertyData?.advancedLayers?.parcelCadastreCentroidItm ?? null,
          }
        );
        console.log('🔗 Plans received:', plansResult);
        
        // Update propertyData with any additional info from plans result
        let finalPropertyData = { ...propertyData };
        if (plansResult) {
          if (plansResult.plans) {
            setSearchResults(plansResult);
          }
          if (plansResult.buildingData) {
            finalPropertyData = { ...finalPropertyData, buildingData: plansResult.buildingData };
            if (plansResult.buildingData.dataSource?.includes('Tel Aviv')) {
              finalPropertyData.municipality = 'תל אביב-יפו';
            }
            console.log('🏛️ Building data merged into propertyInfo:', plansResult.buildingData);
          }
          if (plansResult.reverseLookupAddress && mode === 'parcel') {
            finalPropertyData.fullAddress = plansResult.reverseLookupAddress;
            setSelectedAddress(plansResult.reverseLookupAddress);
          }
        }
        setPropertyInfo(finalPropertyData);

        let taRelevantResult = null;
        let taMappedPlans = [];
        if (
          wantTaRelevant &&
          finalPropertyData?.coordinates &&
          (finalPropertyData?.municipality === 'תל אביב-יפו' || plansResult?.buildingData?.dataSource?.includes('Tel Aviv'))
        ) {
          setLoadingStatus('בודק תוכניות/היתרים בסביבת הכתובת...');
          taRelevantResult = await ArchitecturalPlansService.getTaRelevantForCoordinates(finalPropertyData.coordinates, {
            radiusMeters: 350,
            maxResults: 200,
          });
          if (taRelevantResult) {
            const taMapped = mapTaRelevantToPlanItems(taRelevantResult);
            taMappedPlans = taMapped;
            setSearchResults((prev) => {
              const mergedPlans = [...(prev?.plans || plansResult?.plans || []), ...taMapped];
              const dedup = Array.from(new Map(mergedPlans.map((p) => [p.id || `${p.planNumber || ''}-${p.requestNumber || ''}-${p.title || ''}`, p])).values());
              const filtered = filterTelAvivMunicipalByApplicability(dedup, taRelevantResult);
              return { ...(prev || plansResult || {}), plans: filtered, taRelevant: taRelevantResult };
            });
          }
        }

        // Calculate feasibility
        if (wantMarketFeasibility && finalPropertyData) {
          setLoadingStatus('מבצע ניתוח כדאיות...');
          try {
            // Use real address for pricing API (reverseLookupAddress > urlAddress > gush/helka fallback)
            const addressForPricing = plansResult?.reverseLookupAddress || urlAddress || finalPropertyData?.fullAddress;
            const searchResultsForFeasibility = {
              address: addressForPricing,
              parcelData: finalPropertyData,
              buildingData: finalPropertyData?.buildingData || plansResult?.buildingData,
              plans: plansResult?.plans || [],
              architecturalPlans: [...(plansResult?.plans || []), ...taMappedPlans],
              taRelevant: taRelevantResult,
              municipality: finalPropertyData?.municipality,
              gush: finalPropertyData?.gush,
              helka: finalPropertyData?.helka,
              coordinates: finalPropertyData?.coordinates,
              advancedLayers: finalPropertyData?.advancedLayers
            };
            const feasibilityResult = await FeasibilityService.calculateFeasibilityScore(
              searchResultsForFeasibility,
              {},
              setLoadingStatus
            );
            if (feasibilityResult.success) {
              setFeasibilityData(feasibilityResult);
            }
          } catch (feasibilityError) {
            console.error('Feasibility calculation error:', feasibilityError);
          }
        }
        // Save to search history
        const histAddr = plansResult?.reverseLookupAddress || urlAddress || finalPropertyData?.fullAddress;
        if (histAddr) {
          addSearchHistory({
            address: histAddr,
            gush: finalPropertyData?.gush || urlGush,
            helka: finalPropertyData?.helka || urlHelka,
            municipality: finalPropertyData?.municipality || null,
            searchMode: mode,
          });
        }
      } catch (error) {
        console.error('🔗 Search error:', error);
        const errors = { general: error.message };
        setApiErrors(errors);
        setSearchResults((prev) => ({
          ...(prev || {}),
          sourceHealth: buildSourceHealthFromApiErrors(errors),
        }));
      } finally {
        setLoading(false);
        setLoadingStatus('');
      }
    };

    executeUrlSearch();
  }, [initialUrlState, addSearchHistory]); // Depend on initialUrlState

  // Mobile: auto-scroll just enough to trigger header collapse after search
  useEffect(() => {
    if (!showResults) return;
    if (activeTab !== 'property') return;
    if (typeof window === 'undefined' || window.innerWidth > 768) return;

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 120, behavior: 'smooth' });
    });
  }, [showResults, activeTab]);

  useEffect(() => {
    if (!showResults && activeTab === 'property') {
      setHomepageCardIndex(0);
      requestAnimationFrame(() => {
        const carousel = homepageContextRef.current;
        // Reset the card carousel to the first card horizontally — WITHOUT
        // scrollIntoView, which scrolls the whole homepage down to the cards.
        if (carousel) carousel.scrollLeft = 0;
      });
    }
  }, [showResults, activeTab]);

  const handlePlaceSelected = (place) => {
    if (place.formatted_address) {
      handleAddressChange(place.formatted_address);
    }
  };

  const translateAddressToHebrew = (englishAddress) => {
    let hebrewAddress = englishAddress;
    const streetTranslations = {
      'Tel Aviv-Yafo': 'תל אביב-יפו',
      'Tel Aviv': 'תל אביב',
      'Jerusalem': 'ירושלים',
      'Haifa': 'חיפה'
    };
    Object.entries(streetTranslations).forEach(([english, hebrew]) => {
      const regex = new RegExp(english.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      hebrewAddress = hebrewAddress.replace(regex, hebrew);
    });
    return hebrewAddress.replace(/,?\s*Israel\s*$/i, '').trim();
  };

  const handleSearch = async () => {
    if (searchMode === 'address') {
      const validationError = validateAddress(address);
      if (validationError) {
        setAddressValidationError(validationError);
        AnalyticsService.trackSearchValidationFailed({
          searchMode,
          errorCode: 'invalid_address',
          sourceSurface: 'header_search',
        });
        return;
      }
      setAddressValidationError('');
    }
    
    if (searchMode === 'parcel') {
      if (!gush.trim() || !helka.trim()) {
        setParcelValidationError('נא להזין גוש וחלקה');
        AnalyticsService.trackSearchValidationFailed({
          searchMode,
          errorCode: 'missing_parcel_fields',
          sourceSurface: 'header_search',
        });
        return;
      }
      setParcelValidationError('');
    }

    AnalyticsService.trackSearchStarted(searchMode === 'address' ? address : `גוש ${gush}/${helka}`, {
      searchMode,
      sourceSurface: 'header_search',
    });

    setLoading(true);
    setButtonAnimationComplete(false);
    setSearchKey(prev => prev + 1); // Force loading component to remount fresh
    if (activeTab !== 'property') {
      setActiveTab('property');
    }
    
    setLoadingStatus('מתחיל חיפוש...');
    setShowResults(true);
    setSearchResults(null);
    setPropertyInfo(null);
    setApiErrors({});
    setFeasibilityData(null);
    setFeasibilityOverrides({});
    feasibilityOverridesRef.current = {};
    setAiRecommendation(null);
    setAiLoading(false);
    setAiError(null);
    setResultsSearchMode(searchMode); // Latch the search mode
    setResultsFocus(searchMode === 'address' ? searchFocus : 'all'); // Latch the focus

    // Focus no longer gates fetching: the focus chips retarget live results
    // and the overview cross-links into every subject, so all sections must
    // have data regardless of the focus the search started from.
    const wantMarketFeasibility = true;
    const wantTaRelevant = true;
    
    let propertyData;
    let searchAddress;
    
    if (searchMode === 'parcel') {
      // Start with basic gush/helka data
      propertyData = {
        gush: gush.trim(),
        helka: helka.trim(),
        subHelka: null,
        municipality: null,
        fullAddress: `גוש ${gush}, חלקה ${helka}`,
        searchAddress: `גוש ${gush}, חלקה ${helka}`,
        coordinates: null,
        isMockData: false,
        confidence: 1.0,
        dataSource: 'Direct Parcel Input'
      };
      searchAddress = `גוש ${gush}, חלקה ${helka}`;
      setSelectedAddress(`גוש ${gush}, חלקה ${helka}`);
      setSubmittedAddress(`גוש ${gush}, חלקה ${helka}`);
      
      // Enrich parcel data with coordinates and municipality from GovMap
      try {
        setLoadingStatus('מאתר נתוני חלקה...');
        // Search for parcel to get coordinates
        const parcelSearchResponse = await fetch(`${config.apiBaseUrl}/api/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            searchText: `${gush.trim()} ${helka.trim()}`,
            language: 'he',
            filterType: 'parcel',
            isAccurate: false,
            maxResults: 5
          })
        });
        
        if (parcelSearchResponse.ok) {
          const parcelSearchData = await parcelSearchResponse.json();
          if (parcelSearchData.results && parcelSearchData.results.length > 0) {
            const firstResult = parcelSearchData.results[0];
            // Extract coordinates from shape (POINT format)
            const coordMatch = firstResult.shape?.match(/POINT\(([0-9.]+)\s+([0-9.]+)\)/);
            if (coordMatch) {
              const coords = { x: parseFloat(coordMatch[1]), y: parseFloat(coordMatch[2]) };
              propertyData.coordinates = coords;
              console.log('📍 Got parcel coordinates:', coords);
              
              setLoadingStatus('מאחזר נתוני רשות מקומית...');
              const [advancedData, locateParcel] = await Promise.all([
                GovMapService.fetchAdvancedLayers(coords).catch(() => null),
                GovMapService.getParcelDataByCoordinates(coords).catch(() => null),
              ]);
              if (locateParcel) {
                propertyData.govmapParcelObjectId = locateParcel.objectid;
                propertyData.locateParcelRingWm = locateParcel.locateParcelRingWm;
              }
              if (advancedData) {
                propertyData.municipality = advancedData.municipality;
                propertyData.advancedLayers = advancedData;
                console.log('🏛️ Got advanced data for parcel:', advancedData);
              }
            }
          }
        }
      } catch (error) {
        console.log('⚠️ Could not enrich parcel data:', error.message);
      }
    } else {
      const hebrewAddress = translateAddressToHebrew(address);
      searchAddress = hebrewAddress;
      setSelectedAddress(address);
      setSubmittedAddress(searchAddress); // Update submitted address on search
    }
    
    try {
      const errors = {};
      
      // GovMap Info
      if (searchMode === 'address') {
        setLoadingStatus('מאתר נתוני נכס...');
        try {
          propertyData = await GovMapService.getPropertyInfo(searchAddress);
        } catch (error) {
          console.error('❌ Property info lookup failed:', error);
          errors.propertyInfo = 'לא ניתן להתחבר למאגר מידע קרקע';
          propertyData = null;
        }
      }
      
      // Plans
      let architecturalPlans = null;
      let finalPropertyData = propertyData;
      let plansResult = null;
      let taRelevantResult = null;
      
      if (propertyData && propertyData.gush && propertyData.helka && !propertyData.isMockData) {
        try {
          const prefetchAddr =
            propertyData?.fullAddress ||
            (searchAddress && !String(searchAddress).startsWith('גוש ') ? searchAddress : null);
          if (prefetchAddr && wantMarketFeasibility) prefetchMarketPricing(prefetchAddr);

          // For parcel mode, pass null as searchAddress to trigger reverse lookup for real address
          const searchAddressForPlans = searchMode === 'parcel' ? null : searchAddress;
          plansResult = await ArchitecturalPlansService.getRealArchitecturalPlans(
            propertyData.gush, 
            propertyData.helka,
            propertyData.coordinates,
            searchAddressForPlans,
            setLoadingStatus,
            (partialResult) => {
              if (!partialResult) return;
              setSearchResults(prev => ({
                ...(prev || {}),
                ...partialResult,
                plans: partialResult.plans || prev?.plans || []
              }));
              if (partialResult.buildingData) {
                setPropertyInfo(prev => {
                  if (!prev) return prev;
                  const next = { ...prev, buildingData: partialResult.buildingData };
                  if (partialResult.buildingData.dataSource?.includes('Tel Aviv')) {
                    next.municipality = 'תל אביב-יפו';
                  }
                  return next;
                });
              }
              if (partialResult.reverseLookupAddress && searchMode === 'parcel') {
                setSelectedAddress(partialResult.reverseLookupAddress);
              }
            },
            {
              locateParcelRingWm: propertyData.locateParcelRingWm,
              govmapParcelObjectId: propertyData.govmapParcelObjectId,
              parcelCadastreCentroidItm: propertyData?.advancedLayers?.parcelCadastreCentroidItm ?? null,
            }
          );
          
          if (plansResult && plansResult.plans) {
            architecturalPlans = plansResult.plans;
            if (plansResult.buildingData) {
              finalPropertyData = { ...finalPropertyData, buildingData: plansResult.buildingData };
              if (plansResult.buildingData.dataSource?.includes('Tel Aviv')) {
                finalPropertyData.municipality = 'תל אביב-יפו';
              }
            }
            if (plansResult.reverseLookupAddress && searchMode === 'parcel') {
              finalPropertyData.fullAddress = plansResult.reverseLookupAddress;
              setSelectedAddress(plansResult.reverseLookupAddress);
            }
            // Note: Municipality should come from propertyData.municipality (from govmap-layers API)
            // No need for separate fallback calls
          }
        } catch (error) {
          console.error('❌ Plans lookup failed:', error);
          errors.plans = 'לא ניתן להתחבר למאגר תוכניות';
        }
      }
      
      setPropertyInfo(finalPropertyData);
      
      // Use real address for pricing API (prefer real street address over gush/helka format)
      // Fallback to municipality name if no street address available
      const isGushHelkaFormat = searchAddress?.startsWith('גוש ');
      const addressForPricing = plansResult?.reverseLookupAddress || finalPropertyData?.fullAddress || 
        (isGushHelkaFormat ? finalPropertyData?.municipality : searchAddress);
      
      console.log('🔍 Feasibility address check:', {
        searchAddress,
        isGushHelkaFormat,
        reverseLookupAddress: plansResult?.reverseLookupAddress,
        fullAddress: finalPropertyData?.fullAddress,
        municipality: finalPropertyData?.municipality,
        addressForPricing,
        hasFinalPropertyData: !!finalPropertyData
      });

      const enhancedSearchResults = {
        plans: architecturalPlans || [],
        address: addressForPricing,
        coordinates: propertyData?.coordinates,
        parcelGeometry: plansResult?.parcelGeometry,
        itmCoords: plansResult?.itmCoords,
        parcelData: propertyData?.parcelData || { gushnumber: propertyData?.gush, parcelnumber: propertyData?.helka },
        municipality: finalPropertyData?.municipality,
        municipalityCode: finalPropertyData?.municipalityCode,
        buildingData: finalPropertyData?.buildingData,
        advancedLayers: finalPropertyData?.advancedLayers,
        taRelevant: null,
        sourceHealth: buildSourceHealthFromApiErrors(errors),
      };

      if (
        wantTaRelevant &&
        finalPropertyData?.coordinates &&
        (finalPropertyData?.municipality === 'תל אביב-יפו' || plansResult?.buildingData?.dataSource?.includes('Tel Aviv'))
      ) {
        setLoadingStatus('בודק תוכניות/היתרים בסביבת הכתובת...');
        taRelevantResult = await ArchitecturalPlansService.getTaRelevantForCoordinates(finalPropertyData.coordinates, {
          radiusMeters: 350,
          maxResults: 200,
        });
        if (taRelevantResult) {
          enhancedSearchResults.taRelevant = taRelevantResult;
          const taMapped = mapTaRelevantToPlanItems(taRelevantResult);
          enhancedSearchResults.plans = [...enhancedSearchResults.plans, ...taMapped];
          enhancedSearchResults.plans = Array.from(
            new Map(
              enhancedSearchResults.plans.map((p) => [p.id || `${p.planNumber || ''}-${p.requestNumber || ''}-${p.title || ''}`, p])
            ).values()
          );
          enhancedSearchResults.plans = filterTelAvivMunicipalByApplicability(
            enhancedSearchResults.plans,
            taRelevantResult
          );
        }
      }
      
      // Feasibility
      console.log('🔍 Feasibility condition:', { addressForPricing: !!addressForPricing, finalPropertyData: !!finalPropertyData, willRun: !!(addressForPricing && finalPropertyData) });
      if (wantMarketFeasibility && addressForPricing && finalPropertyData) {
        try {
          console.log('🚀 Running feasibility calculation with address:', addressForPricing);
          const feasibilityResult = await FeasibilityService.calculateFeasibilityScore(
            {
                ...enhancedSearchResults,
                architecturalPlans: architecturalPlans || [],
                taRelevant: taRelevantResult,
                gush: finalPropertyData?.gush,
                helka: finalPropertyData?.helka
            },
            {},
            setLoadingStatus
          );
          console.log('📊 Feasibility result:', { success: feasibilityResult.success, hasInput: !!feasibilityResult.feasibilityInput, marketData: feasibilityResult.feasibilityInput?.marketData });
          if (feasibilityResult.success) {
            setFeasibilityData(feasibilityResult);
          } else {
            console.log('❌ Feasibility failed:', feasibilityResult.error);
          }
        } catch (error) {
          console.error('❌ Feasibility calculation error:', error);
        }
      } else {
        console.log('⏭️ Skipping feasibility - missing data');
      }
      
      setSearchResults(enhancedSearchResults);
      setApiErrors(errors);
      setLoading(false);
      setLoadingStatus('');

      // Save to search history (use local vars, not async state)
      const historyAddress = plansResult?.reverseLookupAddress || searchAddress;
      addSearchHistory({
        address: historyAddress,
        gush: finalPropertyData?.gush || gush.trim(),
        helka: finalPropertyData?.helka || helka.trim(),
        municipality: finalPropertyData?.municipality || null,
        searchMode,
      });

      AnalyticsService.trackSearchCompleted({
        address: historyAddress,
        searchMode,
        gush: finalPropertyData?.gush || gush.trim(),
        helka: finalPropertyData?.helka || helka.trim(),
        municipality: finalPropertyData?.municipality,
        coordinates: finalPropertyData?.coordinates,
        resultsCount: enhancedSearchResults?.plans?.length || 0,
        hasResults: (enhancedSearchResults?.plans?.length || 0) > 0,
        sourceSurface: 'search_flow',
      });
    } catch (error) {
      console.error('❌ Critical search error:', error);
      setApiErrors({ critical: 'שגיאה קריטית בחיפוש' });
      setLoading(false);
      AnalyticsService.trackSearchCompleted({
        address: searchMode === 'address' ? address : `גוש ${gush}/${helka}`,
        searchMode,
        gush: gush.trim(),
        helka: helka.trim(),
        hasResults: false,
        resultsCount: 0,
        sourceSurface: 'search_flow_error',
      });
    }
  };

  const retryPropertyInfo = async () => {
      // Simplified retry logic
      if (!selectedAddress) return;
      try {
        const hebrewAddress = translateAddressToHebrew(selectedAddress);
        const propertyData = await GovMapService.getPropertyInfo(hebrewAddress);
        if (propertyData && !propertyData.isMockData) {
            setPropertyInfo(propertyData);
            // Clear specific error
            const newErrors = {...apiErrors};
            delete newErrors.propertyInfo;
            setApiErrors(newErrors);
        }
      } catch(e) { console.error(e); }
  };

  const retryPlans = async () => {
      // Simplified retry logic
      if (!propertyInfo?.gush) return;
      try {
          const plansResult = await ArchitecturalPlansService.getRealArchitecturalPlans(
            propertyInfo.gush,
            propertyInfo.helka,
            propertyInfo.coordinates,
            selectedAddress,
            null,
            null,
            {
              locateParcelRingWm: propertyInfo.locateParcelRingWm,
              govmapParcelObjectId: propertyInfo.govmapParcelObjectId,
              parcelCadastreCentroidItm: propertyInfo?.advancedLayers?.parcelCadastreCentroidItm ?? null,
            }
          );
          if(plansResult?.plans) {
              setSearchResults(prev => ({...prev, plans: plansResult.plans}));
              const newErrors = {...apiErrors};
              delete newErrors.plans;
              setApiErrors(newErrors);
          }
      } catch(e) { console.error(e); }
  };

  const handleBack = () => {
    setShowResults(false);
    setSearchResults(null);
    setPropertyInfo(null);
    setSelectedAddress('');
    setLoading(false);
    setApiErrors({});
    setFeasibilityData(null);
    setFeasibilityOverrides({});
    feasibilityOverridesRef.current = {};
    setAiRecommendation(null);
    setAiLoading(false);
    setAiError(null);
    setAddress('');
    setGush('');
    setHelka('');
    setSubmittedAddress('');
    setActiveTab('property');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Nav handler: "מידע על נכס" (property) should return to the main search home
  // and clear any open results — not just stay on the current property's results.
  const handleTabChange = (tab) => {
    if (tab === 'property') {
      handleBack();
    } else {
      setActiveTab(tab);
    }
  };

  const handleHistorySelect = useCallback((item) => {
    if (item.searchMode === 'parcel' && item.gush && item.helka) {
      setSearchMode('parcel');
      setGush(item.gush);
      setHelka(item.helka);
      // Auto-trigger search after state updates
      setTimeout(() => {
        // Navigate via URL to trigger search
        const url = new URL(window.location.href);
        url.searchParams.set('gush', item.gush);
        url.searchParams.set('helka', item.helka);
        url.searchParams.delete('address');
        window.location.href = url.toString();
      }, 0);
    } else if (item.address) {
      // Navigate via URL to trigger search — carry the currently-selected focus.
      const url = new URL(window.location.href);
      url.searchParams.set('address', item.address);
      url.searchParams.delete('gush');
      url.searchParams.delete('helka');
      if (searchFocus && searchFocus !== 'all') {
        url.searchParams.set('focus', searchFocus);
      } else {
        url.searchParams.delete('focus');
      }
      window.location.href = url.toString();
    }
  }, [searchFocus]);

  const handleAddressChange = (selectedAddress) => {
    setAddress(selectedAddress);
    setSelectedAddress(selectedAddress);
    if (selectedAddress.trim()) {
      const validationError = validateAddress(selectedAddress);
      setAddressValidationError(validationError || '');
    } else {
      setAddressValidationError('');
    }
  };

  const handleSearchModeToggle = () => {
    setSearchMode(prev => {
      const next = prev === 'address' ? 'parcel' : 'address';
      AnalyticsService.trackSearchModeChanged(next, { sourceSurface: 'search_toggle' });
      return next;
    });
    setAddress('');
    setGush('');
    setHelka('');
    setAddressValidationError('');
    setParcelValidationError('');
  };

  const scrollHomepageCardTo = useCallback((targetIndex) => {
    const carousel = homepageContextRef.current;
    if (!carousel) return;

    const cards = Array.from(carousel.querySelectorAll('.homepage-context-item'));
    if (!cards.length) return;

    const nextIndex = Math.max(0, Math.min(targetIndex, cards.length - 1));
    setHomepageCardIndex(nextIndex);
    cards[nextIndex].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, []);

  const handleHomepageContextScroll = useCallback(() => {
    const carousel = homepageContextRef.current;
    if (!carousel) return;

    const cards = Array.from(carousel.querySelectorAll('.homepage-context-item'));
    if (!cards.length) return;

    const carouselRect = carousel.getBoundingClientRect();
    const carouselCenter = carouselRect.left + carouselRect.width / 2;
    const nearestIndex = cards.reduce((bestIndex, card, index) => {
      const bestRect = cards[bestIndex].getBoundingClientRect();
      const bestDistance = Math.abs((bestRect.left + bestRect.width / 2) - carouselCenter);
      const cardRect = card.getBoundingClientRect();
      const cardDistance = Math.abs((cardRect.left + cardRect.width / 2) - carouselCenter);
      return cardDistance < bestDistance ? index : bestIndex;
    }, 0);

    setHomepageCardIndex((currentIndex) => (currentIndex === nearestIndex ? currentIndex : nearestIndex));
  }, []);

  // Don't autofocus on mobile — it pops the keyboard and scrolls the page down
  // (so navigating home landed scrolled, not at the top).
  const isMobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
  const shouldAutofocusHomepageSearch = !showResults && activeTab === 'property' && !loading && !isMobileViewport;

  // Results view uses the docked (slim) header — its search field is compact, so
  // the loading animation must use its compact variant too.
  const searchTabsActive = !['contact', 'feedback', 'about', 'project-promotion'].includes(activeTab);
  const isDocked = showResults && searchTabsActive;

  // Address/parcel mode toggle — belongs next to the search field (it controls
  // the input). In the docked bar it sits inline beside the search; on the
  // homepage it's the left side of the controls row.
  const searchTypeToggleJSX = (
    <div className="search-type-toggle">
      <button
          className={`search-type-btn ${searchMode === 'address' ? 'active' : ''}`}
          onClick={() => searchMode !== 'address' && handleSearchModeToggle()}
      >
          כתובת
      </button>
      <button
          className={`search-type-btn ${searchMode === 'parcel' ? 'active' : ''}`}
          onClick={() => searchMode !== 'parcel' && handleSearchModeToggle()}
      >
          גוש/חלקה
      </button>
    </div>
  );

  // Focus chips — lead the results with one aspect. On the homepage they sit on
  // the controls row; in the docked view they're the scroll-away strip below.
  const focusChipsJSX = (
    <div className="search-focus-row">
      <span className="search-focus-hint">בחרו נושא להבלטה בתוצאות:</span>
      <div className="search-focus-chips" role="group" aria-label="התמקדות בתוצאות">
        {FOCUS_OPTIONS.map((opt) => (
          <button
            type="button"
            key={opt.id}
            className={`focus-chip ${searchFocus === opt.id ? 'active' : ''}`}
            aria-pressed={searchFocus === opt.id}
            onClick={() => (showResults ? handleResultsFocusChange(opt.id) : setSearchFocus(opt.id))}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );

  // Homepage: the primary mode toggle sits above the field with a "חיפוש לפי"
  // label. The domain chips are a SEPARATE row rendered below the field
  // (see renderSearchBar) — order: toggle → field → chips → recent searches.
  const searchModeRowJSX = (
    <div className="search-mode-row">
      <span className="search-mode-label">חיפוש לפי:</span>
      {searchTypeToggleJSX}
    </div>
  );

  // Custom Render of Search Bar for Header.
  //  - withControls: render the full toggle+chips row above the form (homepage).
  //  - withToggle: render JUST the mode toggle inline beside the form (docked bar).
  const renderSearchBar = ({ withControls = true, withToggle = false } = {}) => (
    <div className={`search-container-wrapper ${withToggle ? 'has-inline-toggle' : ''}`}>
      {withControls && searchModeRowJSX}
      {withToggle && searchTypeToggleJSX}

      <div className={`header-search-form ${(loading || !buttonAnimationComplete) ? 'is-loading' : ''} ${formExpanding ? 'is-expanding' : ''}`}>
        {/* Always render inputs but hide when loading */}
        <div 
          className={`search-form-inputs ${(loading || !buttonAnimationComplete) ? 'hidden' : ''}`}
          style={(loading || !buttonAnimationComplete) ? { display: 'none' } : {}}
        >
          {searchMode === 'address' ? (
            <>
              <div className="header-search-input-group" style={{flex: 2}}>
                  <CustomAutocomplete
                      id="header-address-input"
                      name="address"
                      value={address}
                      onPlaceSelected={handlePlaceSelected}
                      onInputChange={(val) => {
                          setAddress(val);
                          if (addressValidationError) setAddressValidationError('');
                      }}
                      placeholder="כתובת מלאה (לדוגמה: הרצל 10, תל אביב)"
                      className={`header-search-input ${addressValidationError ? 'input-error' : ''}`}
                      isTransparent={true}
                      autoFocus={shouldAutofocusHomepageSearch && searchMode === 'address'}
                  />
              </div>
              {addressValidationError && <div className="validation-error">{addressValidationError}</div>}
            </>
          ) : (
            <>
                <div className="header-search-input-group">
                    <input 
                        id="gush-input"
                        name="gush"
                        type="text" 
                        autoComplete="off"
                        className={`header-search-input ${parcelValidationError ? 'input-error' : ''}`}
                        placeholder="גוש"
                        value={gush}
                        autoFocus={shouldAutofocusHomepageSearch && searchMode === 'parcel'}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^\d*$/.test(val)) setGush(val);
                          if (parcelValidationError) setParcelValidationError('');
                        }}
                    />
                </div>
                <div className="header-search-input-group">
                    <input 
                        id="helka-input"
                        name="helka"
                        type="text" 
                        autoComplete="off"
                        className={`header-search-input ${parcelValidationError ? 'input-error' : ''}`}
                        placeholder="חלקה"
                        value={helka}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^\d*$/.test(val)) setHelka(val);
                          if (parcelValidationError) setParcelValidationError('');
                        }}
                    />
                </div>
                {parcelValidationError && <div className="validation-error">{parcelValidationError}</div>}
            </>
          )}
          
          <button
            className="btn btn-primary header-search-btn"
            onClick={handleSearch}
          >
            חפש
          </button>
        </div>

        {/* Loading overlay that morphs over the form */}
        {(loading || !buttonAnimationComplete) && (
          <SearchInputLoading
            key={searchKey}
            status={loadingStatus}
            isScrolled={isScrolled || isDocked}
            isLoadingComplete={!loading}
            onAnimationComplete={() => {
              // Start expand animation BEFORE removing loading state
              setFormExpanding(true);
              // Delay removing loading state so expand animation can start from shrunk position
              setTimeout(() => {
                setButtonAnimationComplete(true);
              }, 50);
              // Remove expanding class after animation completes
              setTimeout(() => setFormExpanding(false), 500);
            }}
          />
        )}
      </div>

      {/* Domain filters — secondary control row, centered below the field. */}
      {withControls && focusChipsJSX}

      {/* Recent search history — hide via CSS when scrolled to avoid DOM churn */}
      {!loading && searchHistory.length > 0 && (
        <div style={showResults ? { display: 'none' } : undefined}>
          <RecentSearches
            history={searchHistory}
            onSelect={handleHistorySelect}
            onRemove={removeSearchHistory}
            onClear={clearSearchHistory}
          />
        </div>
      )}
    </div>
  );

  const renderHomepageContext = () => (
    <div className="homepage-context-shell" aria-label="מה מקבלים בחיפוש נכס">
      <div className="homepage-context" ref={homepageContextRef} onScroll={handleHomepageContextScroll}>
        <article className="homepage-context-item">
          <div className="homepage-context-copy">
            <h3>כל התכנון במקום אחד</h3>
            <p>תכניות, היתרים וייעודי קרקע מכל הרשויות, מאוחדים, מעודכנים, ובלי לדפדף בעשרה אתרים.</p>
          </div>
          <div className="homepage-context-image">
            <img src="/homepage-card-right.webp" alt="" loading="lazy" decoding="async" />
          </div>
        </article>

        <article className="homepage-context-item">
          <div className="homepage-context-copy">
            <h3>AI שמסביר כדאיות</h3>
            <p>ציון היתכנות לתמ״א ופינוי-בינוי, וה-AI מראה לכם בדיוק איך הגענו לתשובה.</p>
          </div>
          <div className="homepage-context-image">
            <img src="/homepage-card-middle.webp" alt="" loading="lazy" decoding="async" />
          </div>
        </article>

        <article id="homepage-alerts-card" className="homepage-context-item homepage-context-item-alerts">
          <div className="homepage-context-copy">
            <h3>אנחנו עוקבים בשבילכם</h3>
            <p>תכנית, היתר או בקשה חדשה לנכס? מגיע אליכם מייל ברגע שזה קורה, בלי לבדוק כלום ידנית.</p>
          </div>
          <div className="homepage-context-image">
            <img src="/homepage-card-left.webp" alt="" loading="lazy" decoding="async" />
          </div>
        </article>
      </div>
      <div className="homepage-context-controls" aria-hidden="false">
        <button
          type="button"
          onClick={() => scrollHomepageCardTo(homepageCardIndex + 1)}
          disabled={homepageCardIndex >= 2}
          aria-label="גלול שמאלה"
        >
          ←
        </button>
        <button
          type="button"
          onClick={() => scrollHomepageCardTo(homepageCardIndex - 1)}
          disabled={homepageCardIndex <= 0}
          aria-label="גלול ימינה"
        >
          →
        </button>
      </div>
    </div>
  );

  // Determine what content to render based on activeTab and showResults
  const renderContent = () => {
    if (isPlanDetailRoute) {
      return <PlanDetailPage />;
    }

    if (isPermitDetailRoute) {
      return <PermitDetailPage />;
    }

    // Loading Animation Showcase (for preview) - access via ?showcase=loading
    if (window.location.search.includes('showcase=loading')) {
      return <LoadingShowcase />;
    }

    // Contact Page
    if (activeTab === 'contact') {
      return <ContactPage />;
    }

    // Feedback Page
    if (activeTab === 'feedback') {
      return <FeedbackPage />;
    }

    // About Page
    if (activeTab === 'about') {
      return <AboutPage onTabChange={handleTabChange} />;
    }

    if (activeTab === 'project-promotion') {
      return <ProjectPromotionPage />;
    }

    // Terms of Use
    if (activeTab === 'terms') {
      return <TermsOfUse />;
    }

    // Privacy Policy
    if (activeTab === 'privacy') {
      return <PrivacyPolicy />;
    }

    // Property or Feasibility Tabs - Homepage
    if (!showResults) {
      return null; // ScrollVideo rendered separately outside dashboard-container
    }

    // Results View — share button now lives inside ResultsPage (focus strip /
    // summary header), so no standalone header row here.
    return (
      <>
        <div className="results-wrapper">
            {(loading || !buttonAnimationComplete) && !searchResults ? (
                <ResultsPageShimmer focus={resultsFocus} />
            ) : (
            <ResultsPage
                results={searchResults}
                address={submittedAddress}
                propertyInfo={propertyInfo}
                isLoading={loading}
                onBack={handleBack}
                embedded={true}
                apiErrors={apiErrors}
                onRetryPropertyInfo={retryPropertyInfo}
                onRetryPlans={retryPlans}
                feasibilityData={feasibilityData}
                onApplyFeasibilityOverride={applyFeasibilityOverrides}
                feasibilityOverrideBusy={feasibilityOverrideBusy}
                isParcelSearch={resultsSearchMode === 'parcel'}
                focus={resultsFocus}
                onFocusChange={handleResultsFocusChange}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                onShare={handleShare}
                aiRecommendation={aiRecommendation}
                setAiRecommendation={setAiRecommendation}
                aiAnalysisUnlocked={aiAnalysisUnlocked}
                setAiAnalysisUnlocked={setAiAnalysisUnlocked}
                aiLoading={aiLoading}
                setAiLoading={setAiLoading}
                aiError={aiError}
                setAiError={setAiError}
            />
            )}
        </div>
      </>
    );
  };

  return (
    <div className="app">
      {!isDetailRoute ? (
        <Header
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onLogoClick={handleBack}
          disableScrollShrink={!showResults && activeTab === 'property'}
          dockedHeader={isDocked}
          homepageContext={!showResults && activeTab === 'property' ? renderHomepageContext() : null}
        >
          {/* Search bar on property / feasibility tabs. In the docked results
              view the mode toggle stays inline beside the search; the focus
              chips move to the scroll-away strip below. */}
          {searchTabsActive ? (
            renderSearchBar(isDocked ? { withControls: false, withToggle: true } : {})
          ) : null}
        </Header>
      ) : null}

      {/* Docked results view: focus chips in a normal-flow strip that scrolls
          away under the pinned bar (the mode toggle lives in the bar itself). */}
      {!isDetailRoute && isDocked ? (
        <div className="docked-controls-strip">{focusChipsJSX}</div>
      ) : null}

      <div className={`dashboard-container ${!isPlanDetailRoute && !isPermitDetailRoute && !showResults && !['contact','feedback','about','project-promotion'].includes(activeTab) ? 'homepage-mode' : ''}`}>
        <main className="main-content">
          {renderContent()}
        </main>
      </div>

      {!isDetailRoute ? (
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-top">
            <div className="footer-brand">
              <div className="footer-brand-name">
                <img src="/urbanix-logo.svg" alt="UrbaniX.ai" className="footer-brand-logo" />
                <span className="footer-brand-text">UrbaniX<span className="footer-brand-tld">.ai</span></span>
              </div>
              <p className="footer-brand-tagline">המידע התכנוני על הנכס שלך, מאוחד ומוסבר בשפה ברורה.</p>
            </div>
            <div className="footer-columns">
              <div className="footer-col">
                <div className="footer-col-title">מוצר</div>
                <div className="footer-col-links">
                  <button className="footer-col-link" onClick={() => setActiveTab('property')}>מידע על נכס</button>
                  <button className="footer-col-link" onClick={() => setActiveTab('project-promotion')}>קידום פרויקטים</button>
                </div>
              </div>
              <div className="footer-col">
                <div className="footer-col-title">חברה</div>
                <div className="footer-col-links">
                  <button className="footer-col-link" onClick={() => setActiveTab('about')}>אודות</button>
                  <button className="footer-col-link" onClick={() => setActiveTab('contact')}>צור קשר</button>
                  <button className="footer-col-link" onClick={() => setActiveTab('feedback')}>משוב</button>
                </div>
              </div>
              <div className="footer-col">
                <div className="footer-col-title">משפטי</div>
                <div className="footer-col-links">
                  <button className="footer-col-link" onClick={() => openLegalModal('privacy')}>מדיניות פרטיות</button>
                  <button className="footer-col-link" onClick={() => openLegalModal('terms')}>תנאי שימוש</button>
                  <button className="footer-col-link" onClick={() => openLegalModal('accessibility')}>הצהרת נגישות</button>
                </div>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <span className="footer-copyright">© {new Date().getFullYear()} UrbaniX.ai · כל הזכויות שמורות</span>
            <div className="footer-social-links">
              <a
                href="https://www.facebook.com/profile.php?id=61563113456093"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social-link"
                aria-label="Urbanix בפייסבוק"
              >
                <span className="footer-social-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" role="img" focusable="false">
                    <path d="M13.5 8.5V6.7c0-.7.5-1.2 1.2-1.2H16V2h-2c-2.7 0-4.5 1.8-4.5 4.5v2H7v3.5h2.5V22h4V12h2.1l.4-3.5h-2.5z" fill="currentColor"/>
                  </svg>
                </span>
                פייסבוק
              </a>
              <a
                href="https://www.instagram.com/urbanix.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social-link"
                aria-label="Urbanix באינסטגרם"
              >
                <span className="footer-social-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" role="img" focusable="false">
                    <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm0 2.3A2.7 2.7 0 0 0 4.3 7v10A2.7 2.7 0 0 0 7 19.7h10a2.7 2.7 0 0 0 2.7-2.7V7A2.7 2.7 0 0 0 17 4.3H7zm10.8 1.5a1.3 1.3 0 1 1 0 2.6 1.3 1.3 0 0 1 0-2.6zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2.3a2.7 2.7 0 1 0 0 5.4 2.7 2.7 0 0 0 0-5.4z" fill="currentColor"/>
                  </svg>
                </span>
                אינסטגרם
              </a>
              <a
                href="https://www.linkedin.com/company/urbanix-ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social-link"
                aria-label="Urbanix בלינקדאין"
              >
                <span className="footer-social-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" role="img" focusable="false">
                    <path d="M4.98 3.5a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM3.6 9h2.9v11.5H3.6V9zm5 0h2.8v1.6h.04c.4-.74 1.4-1.6 2.9-1.6 3.1 0 3.66 2 3.66 4.6v6.9h-3v-6.1c0-1.46-.03-3.3-2-3.3s-2.3 1.55-2.3 3.2v6.2h-3V9z" fill="currentColor"/>
                  </svg>
                </span>
                לינקדאין
              </a>
            </div>
          </div>
        </div>
      </footer>
      ) : null}

      <LegalModal
        isOpen={legalModalState.isOpen}
        onClose={closeLegalModal}
        title={
          legalModalState.type === 'terms' ? 'תנאי שימוש' : 
          legalModalState.type === 'accessibility' ? 'הצהרת נגישות' : 
          'מדיניות פרטיות'
        }
        content={
          legalModalState.type === 'terms' ? TERMS_OF_USE : 
          legalModalState.type === 'accessibility' ? ACCESSIBILITY_STATEMENT : 
          PRIVACY_POLICY
        }
      />

      {!isDetailRoute && !showResults && activeTab === 'property' ? <DisclaimerBanner /> : null}
      <SpeedInsights />
    </div>
  );
}

export default App;
